import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/services/apiClient";
import { useTranslation } from "react-i18next";
import CustomDropdown from "@/components/ui/CustomDropdown";
import BrandButton from "@/components/ui/BrandButton";
import * as C from "@/const";
import { getLocalStorageWithExpiry, showApiError } from "@/utils";
import { BACKEND_URL } from "@/config";

function QueryResearchSearchBox({
  query,
  setQuery,
  handlePlatformChange,
  handleSearchButton,
  isQueryResearchLoaded,
  queryResearchData,
}) {
  const { t } = useTranslation();
  const clientID = getLocalStorageWithExpiry(C.CLIENT_ID_KEY);

  // History Dropdown State
  const MAX_DISPLAYED_HISTORY_ITEMS = 30;
  const SEARCH_HISTORY_REFETCH_DELAY_MS = 30000; // 30 seconds
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [userHistory, setUserHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [deletingItems, setDeletingItems] = useState(new Set());

  // Filter history based on current query
  useEffect(() => {
    if (query.trim() === "") {
      setFilteredHistory(userHistory);
    } else {
      const filtered = userHistory.filter((historyItem) =>
        historyItem.toLowerCase().includes(query.toLowerCase()),
      );
      setFilteredHistory(filtered);
    }
  }, [query, userHistory]);

  const fetchUserHistory = useCallback(async () => {
    if (!clientID) {
      return;
    }

    try {
      const response = await apiClient.get(
        `${BACKEND_URL}/api/v1/query-research/history`,
      );

      if (response.status === 200) {
        setUserHistory(response.data.history || []);
      }
    } catch (error) {
      console.error("Failed to fetch user history", error);
      setUserHistory([]);
    }
  }, [clientID]);

  // Fetch history on initial load
  useEffect(() => {
    fetchUserHistory();
  }, [fetchUserHistory]);

  // Refetch history after search completes
  useEffect(() => {
    if (!queryResearchData) {
      return;
    }

    // Wait SEARCH_HISTORY_REFETCH_DELAY_MS for BE and Pub/Sub to append the new history item
    const timer = setTimeout(() => {
      fetchUserHistory();
    }, SEARCH_HISTORY_REFETCH_DELAY_MS);

    return () => clearTimeout(timer);
  }, [queryResearchData, fetchUserHistory]);

  // Function to delete a history item
  const deleteHistoryItem = async (queryToDelete) => {
    setDeletingItems((prev) => new Set([...prev, queryToDelete]));

    try {
      await apiClient.delete(`${BACKEND_URL}/api/v1/query-research/history`, {
        data: {
          query_to_be_deleted: queryToDelete,
        },
      });

      setUserHistory((prev) => prev.filter((item) => item !== queryToDelete));
    } catch (error) {
      console.error("Failed to delete history item:", error);
      showApiError(
        t(
          "promptResearch.deleteHistoryError",
          "Failed to delete history item. Please try again.",
        ),
      );
    } finally {
      setDeletingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(queryToDelete);
        return newSet;
      });
    }
  };

  // Handle Enter key press and Escape key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isQueryResearchLoaded && query.trim()) {
      handleSearchButton();
    } else if (e.key === "Escape") {
      handleClearInput();
    }
  };

  const handleClearInput = () => {
    setQuery("");
  };

  return (
    <div
      className="bg-white p-4 card-radius border shadow-sm mb-4"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="row g-3 align-items-center">
        <div className="col-md" data-tour="prompt-research.search">
          <div className="position-relative">
            <div className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted">
              <i className="bi bi-search"></i>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // Prev search is completed and user clicks on Searchbox -- Text is cleared
                if (isQueryResearchLoaded && queryResearchData) {
                  handleClearInput();
                }
                setShowHistoryDropdown(true);
              }}
              onBlur={() => {
                // Delay hiding to allow clicking on dropdown items
                setTimeout(() => setShowHistoryDropdown(false), 150);
              }}
              placeholder={t(
                "promptResearch.searchPlaceholder",
                "Enter your search prompt...",
              )}
              className="form-control ps-5"
              style={{ paddingRight: query ? "2.5rem" : "0.75rem" }}
              disabled={!isQueryResearchLoaded}
            />

            {/* Clear Button */}
            {query && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 p-0 text-muted"
                style={{
                  border: "none",
                  background: "none",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleClearInput}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#dc3545";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6c757d";
                }}
                title={t("common.clear", "Clear search")}
              >
                <i className="bi bi-x-lg" style={{ fontSize: "14px" }}></i>
              </button>
            )}

            {/* History Dropdown */}
            {showHistoryDropdown && filteredHistory.length > 0 && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                style={{
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                  borderColor: "var(--border)",
                }}
              >
                {filteredHistory
                  .slice(0, MAX_DISPLAYED_HISTORY_ITEMS)
                  .map((historyItem, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 history-dropdown-item d-flex align-items-center justify-content-between"
                      style={{
                        cursor: "pointer",
                        borderBottom:
                          index <
                          Math.min(
                            filteredHistory.length,
                            MAX_DISPLAYED_HISTORY_ITEMS,
                          ) -
                            1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <div
                        className="d-flex align-items-center flex-grow-1"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQuery(historyItem);
                          setShowHistoryDropdown(false);
                        }}
                      >
                        <i className="bi bi-clock-history text-muted me-2"></i>
                        <span className="text-truncate">{historyItem}</span>
                      </div>

                      {/* Delete Button */}
                      <button
                        type="button"
                        className="btn btn-sm p-1 text-muted delete-button"
                        style={{
                          border: "none",
                          background: "none",
                          minWidth: "auto",
                          fontSize: "14px",
                        }}
                        disabled={deletingItems.has(historyItem)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await deleteHistoryItem(historyItem);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#dc3545";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                        }}
                        title={t("promptResearch.deleteSearch", "Delete this search")}
                      >
                        {deletingItems.has(historyItem) ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ width: "12px", height: "12px" }}
                          >
                            <span className="visually-hidden">
                              {t("common.loading", "Loading...")}
                            </span>
                          </div>
                        ) : (
                          <i className="bi bi-trash3"></i>
                        )}
                      </button>
                    </div>
                  ))}
                {filteredHistory.length > MAX_DISPLAYED_HISTORY_ITEMS && (
                  <div className="px-3 py-2 text-muted small text-center">
                    {t(
                      "promptResearch.showingLatestHistory",
                      {
                        displayed: MAX_DISPLAYED_HISTORY_ITEMS,
                        total: filteredHistory.length,
                        defaultValue: `Showing ${MAX_DISPLAYED_HISTORY_ITEMS} of ${filteredHistory.length} results`,
                      },
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="col-md-auto" data-tour="prompt-research.engines">
          <CustomDropdown
            options={[
              {
                value: "All GenAI Search Apps",
                label: t(
                  "dashboard.allSearchApps",
                  "All GenAI Search Apps",
                ),
              },
              { value: "chatgpt", label: "ChatGPT" },
              { value: "gemini", label: "Gemini" },
              { value: "claude", label: "Claude" },
              { value: "perplexity", label: "Perplexity" },
            ]}
            defaultValue="All GenAI Search Apps"
            onChange={(val) => handlePlatformChange({ target: { value: val } })}
            disabled={!isQueryResearchLoaded}
          />
        </div>
        <div className="col-md-auto">
          <BrandButton
            onClick={handleSearchButton}
            disabled={!isQueryResearchLoaded || !query.trim()}
          >
            {!isQueryResearchLoaded
              ? t("promptResearch.searching", "Searching...")
              : t("promptResearch.runResearch", "Search")}
          </BrandButton>
        </div>
      </div>
    </div>
  );
}

export default QueryResearchSearchBox;
